// Token storage keys
const TOKEN_KEY = 'auth_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const USER_KEY = 'auth_user';

/**
 * Check if localStorage is available (works in SSR or private browsing)
 */
export function isLocalStorageAvailable(): boolean {
  try {
    const test = '__test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch {
    return false;
  }
}

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  tenantId: string;
}

export interface AuthState {
  user: AuthUser | null;
  token: string | null;
  isAuthenticated: boolean;
}

/**
 * Get stored auth token
 */
export function getToken(): string | null {
  if (!isLocalStorageAvailable()) return null;
  return localStorage.getItem(TOKEN_KEY);
}

/**
 * Store auth token
 */
export function setToken(token: string): void {
  if (!isLocalStorageAvailable()) return;
  localStorage.setItem(TOKEN_KEY, token);
}

/**
 * Clear auth tokens and user data
 */
export function clearAuth(): void {
  if (!isLocalStorageAvailable()) return;
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

/**
 * Get stored user data
 */
export function getUser(): AuthUser | null {
  if (!isLocalStorageAvailable()) return null;
  const userStr = localStorage.getItem(USER_KEY);
  if (!userStr) return null;
  try {
    return JSON.parse(userStr);
  } catch {
    return null;
  }
}

/**
 * Store user data
 */
export function setUser(user: AuthUser): void {
  if (!isLocalStorageAvailable()) return;
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
  return !!getToken();
}

/**
 * Parse JWT token to get expiration time
 * Validates JWT format before parsing (must have 3 parts separated by dots)
 */
export function getTokenExpiration(token: string): number | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null; // Invalid JWT format
    }
    const payload = JSON.parse(atob(parts[1]));
    return payload.exp ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

/**
 * Check if token should be refreshed (expired or expiring within 5 minutes)
 * This returns true when the token is either already expired or will expire
 * within 5 minutes, allowing proactive token refresh before actual expiration.
 */
export function isTokenExpired(token: string): boolean {
  const exp = getTokenExpiration(token);
  if (!exp) return false;
  return Date.now() >= exp - 5 * 60 * 1000;
}

/**
 * Initialize auth state from localStorage
 */
export function getInitialAuthState(): AuthState {
  const token = getToken();
  const user = getUser();

  if (!token || !user) {
    return { user: null, token: null, isAuthenticated: false };
  }

  if (isTokenExpired(token)) {
    clearAuth();
    return { user: null, token: null, isAuthenticated: false };
  }

  return { user, token, isAuthenticated: true };
}
